/**
 * components/AccountMenu.tsx — who you are, what you may reach, and how you leave.
 *
 * `03-components.md` §15 (design-system v6.0.0) is the first contract this panel has ever been held to:
 * before it, `02-patterns.md` §1.3 named the account menu in the bar's anatomy, gave its trigger a size,
 * and said nothing whatever about what the thing is or what it holds. The audit of 2026-08-07 read §15
 * against this file for the first time and closed four findings here. Three were plain. The trigger had no
 * `aria-haspopup`, was 28px where §15.1 and §1.3 both pin 32px (fixed in the stylesheet), and named itself
 * by the signed-in ADDRESS where §15.1 requires the user's name. And the panel carried a **three-way theme
 * control** — which §15.3 names as its worked example of the defect, *"a menu that grew a theme toggle and
 * a language select is a popover wearing a menu's clothes"*. The control is gone; `pages/Settings.tsx` has
 * carried the same three choices, with hints, over the same `App`-held state for as long as this panel
 * has, and region 3 below leads there.
 *
 * ⚠️ **The fourth finding is the shape of the panel, and the argument this file used to make against
 * `role="menu"` was RIGHT about the mechanism and wrong about the conclusion. The mechanism survives
 * here because it is what makes the construction below the correct one rather than a compromise.**
 *
 * `role="menu"` puts NVDA and JAWS into **menu mode**, where only the `menuitem` family is exposed to the
 * reader — everything else in the container is simply not there. §15.2 then requires, inside its own menu,
 * two regions that are expressly not menu items: region 1, the identity block, *"Not interactive, not a
 * menu item"*, and region 6, the running version, *"not a menu item"* and *"selectable text"*. A single
 * `role="menu"` around the whole panel would therefore hide the two regions the clause is most explicit
 * about, from precisely the readers who cannot glance at the corner of the screen to find them. The audit
 * judged that a genuine defect in the clause rather than in this app, and it has been raised with the
 * standard's owner; it is **not** a licence to keep the status quo, and burying identity and the build
 * inside a menu is not an answer either.
 *
 * **So the menu container wraps only the actionable middle.** The panel keeps all six of §15.2's regions
 * in their fixed order; `role="menu"` sits around regions 3, 4 and 5 alone — Settings, Administration
 * where the resolver permits it, and Sign out last after a rule. Region 1 is rendered before that
 * container and region 6 after it, both inside the panel and both **outside** the menu, so a reader in
 * menu mode still meets them by ordinary reading on the way in and on the way out. Three things follow,
 * and each is the point: `aria-haspopup="menu"` on the trigger is now **true** — there is a menu, and it
 * is what the panel opens onto; §5.1's keyboard model applies to the things that really are menu items
 * and to nothing else; and nothing §15.2 requires is lost.
 *
 * **§5.1's model, implemented here rather than described.** A roving `tabindex` — exactly one item is in
 * the tab order and the arrows move it — `ArrowDown`/`ArrowUp` wrapping at both ends, `Home`/`End` to the
 * ends, focus moving into the menu when it opens, `Enter` left to the native `<a>` and `<button>`, and
 * **no hover-open**: the panel opens on click alone. `Escape` is deliberately NOT handled in this file —
 * `useDismissable` owns it at the document, closes the panel and returns focus to the trigger, which is
 * the §5.1 clause `MultiSelect` failed for its whole life and the reason that hook has two callers.
 * (Type-ahead, §5.1's remaining affordance, is not implemented over two or three items; recorded rather
 * than glossed.)
 *
 * The panel is conditionally **MOUNTED** rather than hidden with CSS, so while it is closed it is absent
 * from the DOM and from the tab order both.
 *
 * **The trigger is a raw `<button>` rather than the `Button` primitive.** `ButtonProps` has `expanded` but
 * no slot for `aria-controls` or `aria-haspopup`, and none of its variants, sizes or the loading state
 * apply to a 32px circle of initials. `.multiselect__trigger` is the precedent in this codebase for a raw
 * trigger.
 *
 * **There is no avatar image, anywhere** — and §15.1 now forbids one in this position outright, a
 * photograph being the one treatment that fails at 32px, in both themes, on a phone, beside icon buttons
 * of the same size. `Me.user` carries an id, an address, a display name, a status and the superuser flag;
 * there is no image URL on the wire to render and therefore nothing to fall back from. Initials are the
 * rendering, per §14.
 */
import { useCallback, useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import type { Me, ScopeSummary } from "../lib/api";
import { canReachAdmin } from "../lib/admin";
import { initials, roleLabel } from "../lib/format";
import { VERSION } from "../version";
import { Badge, useDismissable } from "./ui";

/** Referenced by `aria-controls`, so it is stated once rather than typed twice. */
export const ACCOUNT_PANEL_ID = "account-panel";

export interface AccountMenuProps {
  readonly me: Me;
  /** The scope the window is on, or null while none is chosen. */
  readonly activeBu: string | null;
  readonly scopes: readonly ScopeSummary[];
  readonly onSignOut: () => void;
}

/** The menu's items — §15.2's regions 3, 4 and 5, in the order the clause fixes them in. */
type MenuKey = "settings" | "admin" | "signout";

/**
 * The role line — the answer to "am I the superuser", which until this panel existed could only be
 * established by navigating to People and noticing which buttons existed.
 *
 * Three states, and each `title` states the fact the badge carries rather than restating the badge.
 * `Badge` already renders a per-tone glyph, so none of the three is distinguished by colour alone.
 */
function RoleLine({ me }: { readonly me: Me }): JSX.Element {
  if (me.user.isSuperuser) {
    return (
      <p
        className="account__role"
        title="From ADMIN_EMAILS in the deployment, not from a group. Reaches every enabled business unit; removing every group would not change it."
      >
        <Badge tone="warning">Superuser</Badge>
      </p>
    );
  }
  if (canReachAdmin(me)) {
    return (
      <p
        className="account__role"
        title="You can see who has access, the groups and the audit trail. Changes are the superuser's — a hard rail of the permission model, not a grant somebody forgot."
      >
        <Badge>Limited administrator</Badge>
      </p>
    );
  }
  return (
    <p className="account__role">
      <Badge>User</Badge>
    </p>
  );
}

/**
 * The per-scope roles — an answer that only exists in an application whose roles are per scope.
 *
 * It must not render until the response in hand was resolved FOR the active scope. The bootstrap
 * `/api/me` is deliberately scopeless and answers `roles: []` unless exactly one scope is reachable
 * (`CLAUDE.md` §1d), so drawing this from the first answer would tell a genuine author they hold no
 * roles for the length of one fetch — and permanently, for anyone whose second fetch is refused.
 *
 * `roleLabel` rather than `humanise`: the latter renders `bu_admin` as "Bu admin", on a screen whose
 * own role picker says "Business-unit admin".
 */
function ScopeRoles({
  me,
  activeBu,
  scopeName,
}: {
  readonly me: Me;
  readonly activeBu: string | null;
  readonly scopeName: string | null;
}): JSX.Element | null {
  if (activeBu === null || scopeName === null || me.activeBu !== activeBu) return null;
  return (
    <p className="account__scope">
      {me.roles.length === 0
        ? `No roles in ${scopeName}`
        : `${me.roles.map((role) => roleLabel(role)).join(" and ")} in ${scopeName}`}
    </p>
  );
}

export function AccountMenu(props: AccountMenuProps): JSX.Element {
  const { me } = props;
  const [open, setOpen] = useState(false);
  /** Which menu item holds the tab stop. §5.1's roving tabindex: the menu is ONE stop, not three. */
  const [rovingItem, setRovingItem] = useState(0);
  const trigger = useRef<HTMLButtonElement | null>(null);
  const panel = useRef<HTMLDivElement | null>(null);
  const menu = useRef<HTMLDivElement | null>(null);

  const close = useCallback((): void => setOpen(false), []);
  useDismissable(open, close, trigger, panel);

  const scopeName = props.scopes.find((scope) => scope.id === props.activeBu)?.displayName ?? null;
  /** §15.1: the accessible name carries the user's NAME — "account" is what every one of them would be called. */
  const name = me.user.displayName ?? me.user.email;

  // ⚠️ ONE list, from which both the item count and the roving tab stop are derived — and gated by the
  // same `canReachAdmin` call the item itself is. Hand-written indices at each item disagree with the
  // gate the first time somebody adds an entry or changes who may see Administration, and the failure is
  // silent: an index no item holds leaves NOTHING in the tab order, so the menu becomes unreachable by
  // keyboard while looking perfectly correct on screen.
  const keys: readonly MenuKey[] = canReachAdmin(me)
    ? ["settings", "admin", "signout"]
    : ["settings", "signout"];
  // Clamped rather than trusted: a `rovingItem` past the end of a shorter list would put no item in the
  // tab order at all, and a menu no keyboard can enter is a worse failure than a misplaced tab stop.
  const active = Math.min(rovingItem, keys.length - 1);

  /** The two attributes every item shares. Derived from `keys`, so the gate and the tab stop cannot diverge. */
  const item = (key: MenuKey): { readonly role: "menuitem"; readonly tabIndex: number } => ({
    role: "menuitem",
    tabIndex: keys[active] === key ? 0 : -1,
  });

  // Focus follows the roving index, which is also what carries focus INTO the menu when it opens: the
  // toggle resets the index to 0 and this runs on the first render in which the panel exists.
  useEffect(() => {
    if (!open) return;
    const items = menu.current?.querySelectorAll<HTMLElement>('[role="menuitem"]');
    items?.[active]?.focus();
  }, [open, active]);

  const toggle = (): void => {
    setRovingItem(0);
    setOpen((previous) => !previous);
  };

  const onMenuKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    const last = keys.length - 1;
    const move = (next: number): void => {
      // Without this the arrows scroll the page behind the panel and `Home`/`End` jump it to its ends,
      // which on a phone silently takes the panel's anchor with it.
      event.preventDefault();
      setRovingItem(next);
    };
    if (event.key === "ArrowDown") move(active === last ? 0 : active + 1);
    else if (event.key === "ArrowUp") move(active === 0 ? last : active - 1);
    else if (event.key === "Home") move(0);
    else if (event.key === "End") move(last);
    // `Escape` is not handled here on purpose — `useDismissable` closes on it at the document AND returns
    // focus to the trigger. A second handler would be a second decision path over the same key.
  };

  return (
    <div className="account">
      <button
        ref={trigger}
        type="button"
        className="account__trigger"
        // §15.1: a real button, with both. `aria-haspopup="menu"` is truthful because the panel's
        // actionable middle IS a menu — see this file's docblock for why it is not the whole panel.
        aria-haspopup="menu"
        aria-expanded={open}
        // Only while open. An `aria-controls` pointing at an element that does not exist is worse than
        // none: the reader is told there is somewhere to go and there is not.
        {...(open ? { "aria-controls": ACCOUNT_PANEL_ID } : {})}
        aria-label={`Account menu for ${name}`}
        title={me.user.email}
        onClick={toggle}
      >
        <span className="account__avatar" aria-hidden="true">
          {initials(me.user.displayName, me.user.email)}
        </span>
      </button>

      {open ? (
        <div className="account__panel" id={ACCOUNT_PANEL_ID} ref={panel}>
          {/* REGION 1 — identity. Outside the menu, because §15.2 says it is "not a menu item" and menu
              mode would hide it. Region 2 (app-specific context: quota, plan) has no instance here and
              §15.2 permits its absence. */}
          <div className="account__id">
            <p className="account__name">{me.user.displayName ?? me.user.email}</p>
            {/* On a phone this is the first time the signed-in address has ever been visible: the bar's
                identity text was `display:none` below --bp-md. */}
            <p className="account__email">{me.user.email}</p>
            <RoleLine me={me} />
            <ScopeRoles me={me} activeBu={props.activeBu} scopeName={scopeName} />
          </div>

          {/* REGIONS 3, 4 and 5 — and the only part of the panel that is a menu. */}
          <div
            className="account__menu"
            role="menu"
            aria-label="Account actions"
            ref={menu}
            onKeyDown={onMenuKeyDown}
          >
            {/*
              REGION 3 — the user's own destinations. SETTINGS, AND IT IS UNGATED. Every subject has
              settings — the theme, the install, the build — and `App.tsx` lists `settings` among the
              SCOPELESS routes, so it renders before a business unit is chosen and for somebody who has
              none. Since the audit of 2026-08-07 it is also the ONLY place the theme is chosen, §15.3
              having removed the copy that lived in this panel.

              It exists here because the page had NO entry point anywhere in the chrome: routed, tested,
              finished, and reachable only by typing the address. `CLAUDE.md` records that exact failure
              happening twice in this repository — three admin modules, then four more pages — and states
              the lesson as a rule: a module is satisfied by what a user can OPEN, and the check for it is
              a route plus a way in, not a file.

              Not in `primaryNav` either, and that is deliberate rather than an oversight: the rail is the
              application's places of work, `App.mount.test.tsx` asserts its labels are exactly ["Automations", "Goals"]
              or ["Automations", "Goals", "Administration"], and §15.2 puts a user's own destinations here.
            */}
            <a {...item("settings")} className="account__item" href="#/settings" onClick={close}>
              Settings
            </a>

            {/* REGION 4 — privileged destinations, present only where the resolver permits. The hidden
                item is UX and never an enforcement layer: `#/admin` resolves the same gate server-side. */}
            {canReachAdmin(me) ? (
              <a {...item("admin")} className="account__item" href="#/admin" onClick={close}>
                Administration
              </a>
            ) : null}

            {/* REGION 5 — session actions. Last, with a rule above it, and no confirmation: signing out
                costs nothing to undo. */}
            <button
              {...item("signout")}
              type="button"
              className="account__item account__item--signout"
              onClick={() => {
                close();
                props.onSignOut();
              }}
            >
              Sign out
            </button>
          </div>

          {/*
            REGION 6 — app identity, below everything and deliberately quiet. Outside the menu for the
            same reason region 1 is: §15.2 calls it "not a menu item" and "selectable text", and menu mode
            exposes neither. It is the answer to the only question a support conversation ever opens with,
            "what are you running?", and the reason it sits in this panel is that this panel is on every
            screen (`../app-baseline/01-identity.md` §1.3, which owns what the version IS; §15.2 owns
            where it sits).

            Not a `title` on something else, and not console output: both require knowing to look. It is
            plain text a reader can select and paste.
          */}
          <p className="account__version" title={`This build of Ticketless — ${VERSION}`}>
            Ticketless {VERSION}
          </p>
        </div>
      ) : null}
    </div>
  );
}
